import { useMemo } from 'react';
import { Typography } from 'antd';
import { DownloadOutlined, EditOutlined } from '@ant-design/icons';
import saveAs from 'file-saver';
import moment from 'moment-timezone';
import { useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Column, ColumnTypes, DataTable } from 'components';
import { globalActions } from 'app/services/global/slice';
import { Drawers } from 'app/types/drawer';
import { useGetDocumentsQuery } from 'app/services/workers';
import { Worker, WorkerDocument } from 'app/types/worker';
import { useHasPermission } from 'hooks/authorization/useHasPermission';
import { User } from 'constants/index';
import { formatDate } from 'utils/datetime';
import { documentTypeName } from 'utils/worker';
import DeleteDocument from './DeleteButton';

const expiredColor = '#f5222d';

type DocumentRow = WorkerDocument & { children?: WorkerDocument[] };

const newestFirst = (a: WorkerDocument, b: WorkerDocument) =>
  (moment(b.ValidFrom ?? b.CreatedAt).valueOf() - moment(a.ValidFrom ?? a.CreatedAt).valueOf()) ||
  (moment(b.CreatedAt).valueOf() - moment(a.CreatedAt).valueOf());

// One row per document type: the newest document, older ones as expandable sub-rows
const groupByType = (documents: WorkerDocument[]): DocumentRow[] => {
  const byType = new Map<number, WorkerDocument[]>();
  documents.forEach((document) => byType.set(document.DocumentTypeId, [ ...(byType.get(document.DocumentTypeId) ?? []), document ]));

  return Array.from(byType.values()).map((list) => {
    const [ newest, ...older ] = [ ...list ].sort(newestFirst);
    return older.length ? { ...newest, children: older } : newest;
  });
};

type DocumentsTableProps = {
  worker: Worker;
};

const DocumentsTable = ({ worker }: DocumentsTableProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { data, isLoading, isFetching } = useGetDocumentsQuery(worker.Id);
  const canManageDocuments = useHasPermission(User.Permissions.MANAGE_WORKER_DOCUMENTS);

  const rows = useMemo(() => groupByType(data ?? []), [ data ]);

  const handleDownload = (document: WorkerDocument) => {
    if (!document.FileUrl) return;
    const fileName = document.FileName ? document.FileName.replaceAll('"', '') : 'document';
    saveAs(document.FileUrl, fileName);
  };

  const handleEdit = (document: WorkerDocument) => {
    dispatch(globalActions.openDrawer({
      key: Drawers.EDIT_WORKER_DOCUMENT,
      props: { document, worker }
    }));
  };

  const columns: Column[] = [
    {
      key: 'DocumentTypeName',
      title: t('entities.workerdocument.documenttypeid'),
      type: ColumnTypes.CUSTOM,
      hasSort: true,
      renderAs: (document: WorkerDocument) => documentTypeName({ Code: document.DocumentTypeCode, Name: document.DocumentTypeName }),
    },
    {
      key: 'FileName',
      title: t('entities.workerdocument.file'),
      type: ColumnTypes.CUSTOM,
      renderAs: (document: WorkerDocument) => document.FileUrl
        ? <Typography.Link onClick={() => handleDownload(document)}>{document.FileName}</Typography.Link>
        : '-',
    },
    {
      key: 'ValidFrom',
      title: t('entities.workerdocument.validfrom'),
      type: ColumnTypes.DATE,
      hasSort: true,
    },
    {
      key: 'ValidTo',
      title: t('entities.workerdocument.validto'),
      type: ColumnTypes.CUSTOM,
      hasSort: true,
      renderAs: (document: WorkerDocument) => document.ValidTo
        ? <Typography.Text type={document.IsExpired ? 'danger' : undefined}>{formatDate(document.ValidTo)}</Typography.Text>
        : '-',
    },
  ];

  const actions: any[] = [ {
    icon: <DownloadOutlined />,
    toolTip: t('entities.workerdocument.download'),
    onClick: handleDownload,
    hidden: (document: WorkerDocument) => !document.FileUrl
  } ];

  if (canManageDocuments) {
    actions.unshift((document: WorkerDocument) => <DeleteDocument document={document} />);

    actions.unshift({
      icon: <EditOutlined />,
      toolTip: t('common.editbytype', { type: t('entities.workerdocument.title').toLowerCase() }),
      onClick: handleEdit
    });
  }

  columns.push({
    key: 'Action',
    title: t('common.action'),
    type: ColumnTypes.ACTIONS,
    actions: actions
  });

  return <DataTable
    data={rows}
    columns={columns}
    isLoading={isLoading || isFetching}
    pagination={true}
    tableKey="workerdocuments"
    rowStatusColor={(document: WorkerDocument) => (document.IsExpired ? expiredColor : undefined)}
  />;
};

export default DocumentsTable;
